import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { LoadingButton } from '../components/common/LoadingIndicators';
import { Alert, Input, Card, Spinner, GoogleIcon } from '../components/ui';
import { formatAuthError } from '../utils/errorFormatting';
import ErrorDisplay from '../components/common/ErrorDisplay';
import { toAppError } from '../types/api';
import Logger from '../utils/logger';

const logger = Logger.create('LoginPage');

const LoginPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signIn, signInWithGoogle, currentUser, loading } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | string | null>(null);
  const message = location.state?.message;

  // Redirect if already logged in
  useEffect(() => {
    if (currentUser && !loading) {
      navigate('/main');
    }
  }, [currentUser, loading, navigate]);

  // Studio SSO: Designer has no local login form — redirect unauthenticated
  // users to Studio's hosted login. signIn() (StudioAuthProvider) navigates
  // away via window.location, so guard against re-firing on re-render.
  const isStudioSso = import.meta.env.VITE_AUTH_PROVIDER === 'studio_sso';
  const studioRedirectStarted = useRef(false);
  useEffect(() => {
    if (
      isStudioSso &&
      !loading &&
      !currentUser &&
      !studioRedirectStarted.current
    ) {
      studioRedirectStarted.current = true;
      void signIn();
    }
  }, [isStudioSso, loading, currentUser, signIn]);

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError('Please enter both email and password.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await signIn(email, password);
      // Navigation will happen automatically via useEffect when currentUser updates
    } catch (err) {
      logger.error('Login error:', err);
      setError(toAppError(err, 'auth'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await signInWithGoogle();
      // Navigation will happen automatically via useEffect when currentUser updates
    } catch (err) {
      logger.error('Google login error:', err);
      setError(toAppError(err, 'auth'));
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading spinner while checking auth state
  if (loading) {
    return (
      <div className="min-h-[80vh] flex flex-col items-center justify-center">
        <Spinner size="lg" className="text-primary-600" />
        <p className="mt-4 text-secondary-600">Loading...</p>
      </div>
    );
  }

  // Studio SSO: never render the local Firebase form — the effect above
  // redirects to Studio. Show a redirecting state while window.location swaps.
  if (isStudioSso) {
    return (
      <div className="min-h-[80vh] flex flex-col items-center justify-center">
        <Spinner size="lg" className="text-primary-600" />
        <p className="mt-4 text-secondary-600">Redirecting to sign in…</p>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4">
      <Card className="w-full max-w-md" variant="elevated" padding="lg">
        <h1
          id="login-title"
          className="text-2xl font-bold text-center text-secondary-900 mb-6"
        >
          Sign In
        </h1>

        {message && (
          <Alert variant="success" className="mb-4" role="status">
            {message}
          </Alert>
        )}

        <ErrorDisplay
          error={error}
          formattedError={error ? formatAuthError(error) : undefined}
          showClose={true}
          onClose={() => setError(null)}
          className="mb-4"
        />

        <form
          onSubmit={handleEmailLogin}
          role="form"
          aria-labelledby="login-title"
        >
          <div className="space-y-4">
            <Input
              id="email"
              label="Email Address"
              type="email"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              required
              fullWidth
              aria-describedby={error ? 'login-error' : undefined}
            />
            <Input
              id="password"
              label="Password"
              type="password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              required
              fullWidth
              aria-describedby={error ? 'login-error' : undefined}
            />
          </div>

          <LoadingButton
            type="submit"
            className="w-full mt-6"
            disabled={!email.trim() || !password.trim()}
            loading={isLoading}
            loadingText="Signing In..."
            variant="primary"
            size="lg"
            aria-describedby="login-help"
          >
            Sign In
          </LoadingButton>

          <div className="flex items-center my-6">
            <div className="flex-1 border-t border-secondary-300" />
            <span className="px-4 text-sm text-secondary-500">OR</span>
            <div className="flex-1 border-t border-secondary-300" />
          </div>

          <LoadingButton
            type="button"
            className="w-full"
            variant="outline"
            size="lg"
            leftIcon={<GoogleIcon className="w-5 h-5" />}
            onClick={handleGoogleLogin}
            loading={isLoading}
            loadingText="Signing In..."
            aria-label="Sign in with Google account"
          >
            Sign in with Google
          </LoadingButton>

          <div className="flex justify-center gap-2 mt-6">
            <span className="text-sm text-secondary-600">
              Don't have an account?
            </span>
            <RouterLink
              to="/signup"
              className="text-sm text-primary-600 hover:text-primary-700 hover:underline"
            >
              Sign up
            </RouterLink>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default LoginPage;
